import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
const creditPackages = [
    { id: 'credits_10', credits: 10, bonusCredits: 0, price: 1 },
    { id: 'credits_50', credits: 50, bonusCredits: 5, price: 20 },
    { id: 'credits_100', credits: 100, bonusCredits: 15, price: 35 },
    { id: 'credits_250', credits: 250, bonusCredits: 50, price: 75 }
];
export const CreditBadges = ({ onPurchaseSuccess, hasActiveSubscription = false }) => {
    const [processingPackage, setProcessingPackage] = useState(null);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const handleBadgeClick = (pkg) => {
        if (!hasActiveSubscription) {
            alert('Credits can only be purchased with an active subscription. Please subscribe to a plan first.');
            return;
        }
        setSelectedPackage(pkg);
        setShowPaymentForm(true);
    };
    const handlePurchase = async () => {
        if (!stripe || !elements || !selectedPackage)
            return;
        setProcessingPackage(selectedPackage.id);
        // console.log(`[CreditBadges] Purchasing ${selectedPackage.credits} credits for $${selectedPackage.price}`);
        try {
            // Create payment method
            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                throw new Error('Card element not found');
            }
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
            });
            if (pmError) {
                throw new Error(pmError.message);
            }
            // Purchase credits using the same API as billing page
            const response = await fetch('/api/billing/credits-purchase', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    packageId: selectedPackage.id,
                    paymentMethodId: paymentMethod.id,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Purchase failed');
            }
            const result = await response.json();
            if (result.success && result.clientSecret) {
                // Confirm payment using Stripe
                const confirmResult = await stripe.confirmCardPayment(result.clientSecret, {
                    payment_method: paymentMethod.id
                });
                if (confirmResult.error) {
                    throw new Error(confirmResult.error.message);
                }
                if (confirmResult.paymentIntent.status === 'succeeded') {
                    // Payment succeeded
                    onPurchaseSuccess(selectedPackage.credits);
                    setShowPaymentForm(false);
                    setSelectedPackage(null);
                    // Clear card element
                    cardElement.clear();
                }
                else {
                    throw new Error('Payment was not completed successfully');
                }
            }
            else {
                throw new Error(result.error || 'Purchase failed');
            }
        }
        catch (error) {
            console.error('[CreditBadges] Purchase error:', error);
            alert(`Purchase failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        finally {
            setProcessingPackage(null);
        }
    };
    const handleCancelPayment = () => {
        setShowPaymentForm(false);
        setSelectedPackage(null);
        setProcessingPackage(null);
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex items-center space-x-2", children: creditPackages.map((pkg) => (_jsx("button", { onClick: () => handleBadgeClick(pkg), disabled: processingPackage !== null || !hasActiveSubscription, className: `
              relative min-w-[32px] h-6 px-2 text-xs font-medium rounded-full transition-all duration-200
              ${!hasActiveSubscription
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : processingPackage === pkg.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700'}
              ${processingPackage !== null && processingPackage !== pkg.id
                        ? 'opacity-50 cursor-not-allowed'
                        : hasActiveSubscription ? 'hover:shadow-sm' : ''}
            `, title: !hasActiveSubscription
                        ? 'Credits can only be purchased with an active subscription'
                        : pkg.bonusCredits > 0
                            ? `Buy ${pkg.credits} credits + ${pkg.bonusCredits} bonus (${pkg.credits + pkg.bonusCredits} total) for $${pkg.price}`
                            : `Buy ${pkg.credits} credits for $${pkg.price}`, children: processingPackage === pkg.id ? (_jsx("div", { className: "flex items-center justify-center", children: _jsx("div", { className: "w-2 h-2 border border-white border-t-transparent rounded-full animate-spin" }) })) : (pkg.credits) }, pkg.id))) }), showPaymentForm && selectedPackage && (_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50", children: _jsxs("div", { className: "bg-white rounded-lg p-6 max-w-sm w-full", children: [_jsxs("h3", { className: "text-lg font-medium text-gray-900 mb-4", children: ["Invest in ", selectedPackage.credits + selectedPackage.bonusCredits > selectedPackage.credits ? `${selectedPackage.credits + selectedPackage.bonusCredits}` : `${selectedPackage.credits}`, " Credits"] }), _jsxs("div", { className: "mb-4 p-3 bg-blue-50 rounded-lg", children: [_jsxs("div", { className: "space-y-1", children: [_jsxs("div", { className: "flex justify-between items-center", children: [_jsx("span", { className: "text-sm text-gray-600", children: "Credits" }), _jsx("span", { className: "font-medium text-gray-900", children: selectedPackage.credits })] }), selectedPackage.bonusCredits > 0 && (_jsxs("div", { className: "flex justify-between items-center", children: [_jsx("span", { className: "text-sm text-gray-600", children: "Bonus" }), _jsxs("span", { className: "font-semibold text-green-600", children: ["+", selectedPackage.bonusCredits] })] })), selectedPackage.bonusCredits > 0 && (_jsxs("div", { className: "flex justify-between items-center border-t pt-1 mt-1", children: [_jsx("span", { className: "text-sm font-medium text-gray-700", children: "Total" }), _jsx("span", { className: "font-semibold text-blue-600", children: selectedPackage.credits + selectedPackage.bonusCredits })] }))] }), _jsxs("div", { className: "mt-2 pt-2 border-t border-blue-200", children: [_jsxs("div", { className: "flex justify-between items-center", children: [_jsx("span", { className: "text-sm font-semibold text-blue-900", children: "Price" }), _jsxs("span", { className: "font-bold text-blue-900", children: ["$", selectedPackage.price] })] }), _jsxs("div", { className: "flex justify-between items-center", children: [_jsx("span", { className: "text-xs text-blue-700", children: "Per Credit" }), _jsxs("span", { className: "text-xs text-blue-700", children: ["$", (selectedPackage.price / (selectedPackage.credits + selectedPackage.bonusCredits)).toFixed(3)] })] })] })] }), selectedPackage.bonusCredits > 0 && (_jsx("div", { className: "mb-4 p-2 bg-green-50 border border-green-200 rounded-lg", children: _jsxs("p", { className: "text-xs font-medium text-green-800 text-center", children: ["\uD83C\uDF81 Get ", selectedPackage.bonusCredits, " bonus credits FREE!"] }) })), _jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "Card Information" }), _jsx("div", { className: "p-3 border border-gray-300 rounded-lg", children: _jsx(CardElement, { options: {
                                            style: {
                                                base: {
                                                    fontSize: '16px',
                                                    color: '#424770',
                                                    '::placeholder': {
                                                        color: '#aab7c4',
                                                    },
                                                },
                                            },
                                        } }) })] }), _jsxs("div", { className: "flex space-x-3", children: [_jsx("button", { onClick: handleCancelPayment, className: "flex-1 py-2 px-4 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50", children: "Cancel" }), _jsx("button", { onClick: handlePurchase, disabled: !stripe || !elements || processingPackage !== null, className: "flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50", children: processingPackage ? 'Processing...' : `Buy $${selectedPackage.price}` })] })] }) }))] }));
};
